// @givmo/compliance — the compliance module's registry-served legal line (CCM-WEB1; CCM-R1-LAW-DISC).
//
// ══ WHAT IS SERVED, AND WHERE IT GOES ═══════════════════════════════════════════════════════════
//
// The cite-or-drop pass (plan §3, D-CCM-19) retired the fourteen CCM-D1..D14 disclosure classes.
// The module keeps ONE legal line: plan §3 row 7's not-a-substitute sentence, as Jonathan approved
// it. It is served by the backend's versioned disclosure registry under ONE surface key,
// `COMPLIANCE_NOT_A_SUBSTITUTE` (`GET /disclaimers/resolve?surface=…`), and this module renders the
// registry's lines verbatim. It hardcodes no legal copy — not even in this comment: the bytes live
// in the registry, which is what keeps every surface (this console, the notices, the connector)
// saying the same thing, and `compliance-copy-law.test.ts` holds every source file to that.
//
// Row 7 places it on the DECISION-SHAPED surfaces only, beside the output, in body-size type, never
// behind a link or a hover. On this console (CCM-R1-INTAKE (a); CCM-R1-DOCS; CCM-R1-SIGN-WEB; CCM-R1-SUBMIT-WEB;
// CCM-R1-REVIEW-WEB) that is these:
//   • `obligation-detail` — the filing panel: directly under the date block, wherever it shows a
//     worked-out date or its basis;
//   • `calendar` — the Calendar: one line at its foot, while a library filing's worked-out date or
//     basis shows (A-X1);
//   • `add-filing` — the add sheet, directly under its "Next due" card wherever that card shows a
//     worked-out date or its basis (CCM-R1-INTAKE-NEXTDUE, frames 03 and 04), the year-end question
//     included; none for a filing Givmo has no date for;
//   • `review` — the review (CCM-R1-REVIEW-WEB; D7 (A)): once, in its decisions card, directly above
//     the act, as the signer page carries it above its own;
//   • `filing-workbench` — a prepared form: directly under its page (slice 2 §5.1), beside the
//     output and never in the document's bytes;
//   • `signer-page` — the pre-signing screen (row 7, place 4; slice 2 §6(b), frame 38): in the
//     signature panel, above the act's words (CCM-R1-SIGN-WEB);
//   • `send-review` — the send review (slice 3 §5.2, O4 (A); CCM-R1-SUBMIT-WEB): once, under the
//     package, until sending starts. Never in the email, the outcomes or the panel's Sent line.
//   • `handoff` — sending it yourself (slice 3 §10, O4 (A); CCM-R1-SUBMIT-HANDOFF): once, under the
//     first step, the package of the prepared forms. Never on the record of what was sent.
// The organization's own records and documents carry NO sentence (Filings, Documents, an own
// filing), so those have no slot at all.
//
// The line is the approved reference's plain paragraph (round-6 frames 07/09: `text-s13 leading-h15
// text-pretty text-text-secondary`, no heading, no box), not the boxed donor-disclosure block.
//
// ══ UNTIL THE ROW IS SERVED ═════════════════════════════════════════════════════════════════════
//
// The surface key ships before its registry row (the backend's reader-first seam: a row on a label
// the previous revision does not know would 500 that revision's unfiltered reads). Until
// CCM-R1-LAW-DISC-B seeds the row, the resolve answers 404 — or 400 on a backend that predates the
// key — and every state below renders NOTHING: no binding change is needed when the row lands.

import { useEffect, useState } from 'react';
import { useCompliance } from './compliance-context';

/** The console surfaces that carry row 7's sentence — the decision-shaped ones, and only those. */
export type ComplianceSurface =
  | 'obligation-detail'
  | 'calendar'
  | 'add-filing'
  | 'review'
  | 'filing-workbench'
  | 'signer-page'
  | 'send-for-signature'
  | 'send-review'
  | 'handoff';

/** The ONE registry surface key the module's legal line resolves from. */
export const COMPLIANCE_DISCLOSURE_REGISTRY_SURFACE = 'COMPLIANCE_NOT_A_SUBSTITUTE';

/** What it takes to render the served line on one console surface: the registry key that serves it. */
export interface ComplianceDisclosureBinding {
  /** A member of the backend's disclosure `Surface` enum. */
  registrySurface: string;
}

/**
 * Console surface → the registry binding that serves its line. Every decision-shaped surface binds
 * the ONE surface key; a record surface is not a `ComplianceSurface` at all.
 */
export const COMPLIANCE_DISCLOSURE_SURFACES: Readonly<
  Record<ComplianceSurface, ComplianceDisclosureBinding>
> = {
  'obligation-detail': { registrySurface: COMPLIANCE_DISCLOSURE_REGISTRY_SURFACE },
  calendar: { registrySurface: COMPLIANCE_DISCLOSURE_REGISTRY_SURFACE },
  'add-filing': { registrySurface: COMPLIANCE_DISCLOSURE_REGISTRY_SURFACE },
  // The review, in its decisions card above the act (D7 (A)): one act decides what is shown.
  review: { registrySurface: COMPLIANCE_DISCLOSURE_REGISTRY_SURFACE },
  'filing-workbench': { registrySurface: COMPLIANCE_DISCLOSURE_REGISTRY_SURFACE },
  'signer-page': { registrySurface: COMPLIANCE_DISCLOSURE_REGISTRY_SURFACE },
  // A pre-signing screen (row 7 (4)): the sheet that sends a form out for signature.
  'send-for-signature': { registrySurface: COMPLIANCE_DISCLOSURE_REGISTRY_SURFACE },
  // The send review, under the package (O4 (A)): the last look before a filing leaves.
  'send-review': { registrySurface: COMPLIANCE_DISCLOSURE_REGISTRY_SURFACE },
  // Sending it yourself, under the first step (O4 (A)): the package of the prepared forms.
  handoff: { registrySurface: COMPLIANCE_DISCLOSURE_REGISTRY_SURFACE },
};

/**
 * Render the registry-served line for one decision-shaped console surface.
 *
 * Three states render nothing: the row is absent (404 — the state until the backend seeds it),
 * the resolve serves empty lines, or the resolve fails. A legal-copy read that errors must never
 * become a toast, an error boundary, or a stand-in sentence. The one state that renders anything is
 * served lines, rendered verbatim as the approved plain paragraph.
 *
 * ⚠ No `floor` is passed: a compiled-in floor would be legal copy pinned INTO the bundle, which is
 * the registry's job. Floor-less means a failed or absent resolve renders nothing.
 */
export function ComplianceDisclosure({ surface }: { surface: ComplianceSurface }) {
  const { operations } = useCompliance();
  const binding = COMPLIANCE_DISCLOSURE_SURFACES[surface];
  const [lines, setLines] = useState<readonly string[]>([]);

  useEffect(() => {
    let cancelled = false;
    void operations
      .resolveDisclosure(binding.registrySurface)
      .then((resolvedLines) => {
        if (!cancelled) setLines(resolvedLines ?? []);
      })
      .catch(() => {
        // Deliberately silent. An unavailable legal-copy read renders nothing; it never degrades to
        // this app's own words and never interrupts the surface it sits on.
        if (!cancelled) setLines([]);
      });
    return () => {
      cancelled = true;
    };
  }, [binding, operations]);

  const served = lines.filter((line) => line.trim().length > 0);
  if (served.length === 0) return null;
  return (
    <div className="flex flex-col gap-4" data-testid={`compliance-disclosure-${surface}`}>
      {served.map((line, index) => (
        <p key={index} className="text-s13 leading-h15 text-pretty text-text-secondary">
          {line}
        </p>
      ))}
    </div>
  );
}
